import { formatPersonFullName } from '@shared/utils/person-full-name.util';

import type { ManagedTeacherStatus, ManagedTeacherUser } from '../models/managed-teacher-user';

const MANAGED_TEACHER_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Formats an ISO timestamp for display in the user-management UI.
 *
 * @param isoDate - Parseable date string from the API.
 * @param locale - Active Transloco language code.
 * @returns Localized date-time string.
 */
export function formatManagedTeacherDateTime(isoDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, MANAGED_TEACHER_DATE_FORMAT).format(new Date(isoDate));
}

/**
 * Returns the managed teacher's full name in locale-appropriate order.
 *
 * @param user - Domain user row.
 * @param activeLang - Active Transloco language code.
 * @returns Display full name.
 */
export function managedTeacherFullName(user: ManagedTeacherUser, activeLang: string): string {
  return formatPersonFullName(user.firstName, user.lastName, activeLang);
}

/**
 * CSS modifier segment for a managed-teacher status badge.
 *
 * @param status - Domain status value.
 * @returns Lowercase modifier for BEM class names.
 */
export function managedTeacherStatusModifier(status: ManagedTeacherStatus): string {
  switch (status) {
    case 'Active': {
      return 'active';
    }
    case 'Invited': {
      return 'invited';
    }
    case 'Inactive': {
      return 'inactive';
    }
    default: {
      return 'inactive';
    }
  }
}

/**
 * Whether a user row matches a case-insensitive search term.
 *
 * @param user - Domain user row.
 * @param searchTerm - Raw search input.
 * @param activeLang - Active Transloco language code (for full-name matching).
 * @returns True when the term matches name, email, role, or status.
 */
export function matchesUserSearch(user: ManagedTeacherUser, searchTerm: string, activeLang: string): boolean {
  const normalized = searchTerm.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }

  const haystack = [
    user.firstName,
    user.lastName,
    managedTeacherFullName(user, activeLang),
    user.email,
    user.role,
    user.status,
  ]
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalized);
}
